import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

function formatDetail(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setUser(data.user);
      toast.success("Password updated");
      navigate("/");
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl border border-slate-200/70 p-8" data-testid="reset-form">
        <div className="flex items-center gap-2 mb-6">
          <img src="/al-axe-mark.svg" alt="" aria-hidden="true" className="h-9 w-auto" />
          <div className="font-semibold text-lg tracking-tight">AL-AXE</div>
        </div>
        <h2 className="text-3xl font-semibold tracking-tight">Choose a new password</h2>
        <p className="text-slate-500 text-sm mt-1 mb-6">Min 8 characters · at least 1 letter + 1 number.</p>

        {!token && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-800 p-4 text-sm mb-4">
            This reset link is missing its token. Request a new one from the <Link to="/forgot-password" className="underline">forgot password</Link> page.
          </div>
        )}

        <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">New password</label>
        <input data-testid="reset-password" value={password} onChange={(e)=>setPassword(e.target.value)} required minLength={8} type="password" autoComplete="new-password" className="mt-1 mb-4 w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-400" />

        <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Confirm password</label>
        <input data-testid="reset-confirm" value={confirm} onChange={(e)=>setConfirm(e.target.value)} required minLength={8} type="password" autoComplete="new-password" className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-400" />

        <button data-testid="reset-submit" disabled={busy || !token} className="mt-6 w-full al-gradient text-white font-medium py-3 rounded-full disabled:opacity-60 active:scale-[0.98]">
          {busy ? "Saving…" : "Save new password"}
        </button>
        <div className="mt-6 text-sm text-slate-500 text-center">
          <Link to="/login" className="hover:underline">← Back to sign in</Link>
        </div>
      </form>
    </div>
  );
}
